import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  CartesianViewportHandle,
  FigureElement,
  FigureHandle,
  FormulaLegendHandle,
  ReadoutHandle,
  ReadoutRow,
} from '@learnforge/demo-render';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import { createCartesianViewport, createFormulaLegend, createSvgEl } from '@learnforge/demo-render';
import type { CalculusContext } from '../types';
import type { CalcKey } from '../locales/keys';
import { createCurveLayer, type CurveLayerHandle } from '../render/curve';
import { compileFn, sampleFn, oneSidedLimit, classifyDiscontinuity } from '../calc';
import { LIMIT_PRESETS } from '../data/limit-presets';
import { createCartesianPlane, type CartesianPlaneHandle } from '../render/cartesian-plane';

// ─── Stage layout constants ───────────────────────────────────────────────────

/** Stage width and height in pixels = world coords × UNIT. */
const STAGE_W = 800;
const STAGE_H = 460;
const BASE_DOMAINS = {
  xDomain: [-10, 10] as [number, number],
  yDomain: [-5.75, 5.75] as [number, number],
};
const PLOT_RECT = { x: 0, y: 0, width: STAGE_W, height: STAGE_H };
/** Number of sample points for the function curve. */
const SAMPLE_N = 400;
/** Small offset from the approach point for left/right approach dots. */
const APPROACH_OFFSET = 0.25;

// ─── State ────────────────────────────────────────────────────────────────────

export interface LimitLabState {
  presetId: string;
  /** The x-value the cursor is approaching (in math coordinates). */
  approach: number;
  showEpsilonDelta: boolean;
  epsilon: number;
}

// ─── Pure helpers (exported and tested) ──────────────────────────────────────

/**
 * Build the four readout rows for the current limit-lab state.
 *
 * Rows:
 *  - `limit-left`  : lim x→a⁻  (left one-sided limit)
 *  - `limit-right` : lim x→a⁺  (right one-sided limit)
 *  - `f-at-a`      : f(a)       (function value at the approach point, flagged 'broken' when undefined/hole)
 *  - `kind`        : discontinuity classification
 */
export function limitReadoutModel(state: LimitLabState): ReadoutRow[] {
  const preset = LIMIT_PRESETS.find((p) => p.id === state.presetId) ?? LIMIT_PRESETS[0]!;
  const a = preset.point;
  const f = compileFn(preset.source);

  const L = oneSidedLimit(f, a, 'left');
  const R = oneSidedLimit(f, a, 'right');
  const kind = classifyDiscontinuity(f, a);

  let fa: number;
  try {
    fa = f(a);
  } catch {
    fa = NaN;
  }

  const formatNum = (v: number): string => {
    if (Number.isNaN(v)) return 'undefined';
    if (!Number.isFinite(v)) return v > 0 ? '+∞' : '−∞';
    return v.toFixed(3);
  };

  // f(a) is "broken" (hole) when:
  //   - f(a) is not finite, OR
  //   - the two-sided limit exists (L ≈ R, both finite) but f(a) ≠ L
  const limitsAgree = Number.isFinite(L) && Number.isFinite(R) && Math.abs(L - R) < 1e-6;
  const faIsBroken = !Number.isFinite(fa) || (limitsAgree && Math.abs(fa - L) > 1e-6);

  return [
    {
      id: 'limit-left',
      lhs: 'lim x→a⁻',
      relation: '=' as const,
      rhs: formatNum(L),
    },
    {
      id: 'limit-right',
      lhs: 'lim x→a⁺',
      relation: '=' as const,
      rhs: formatNum(R),
    },
    {
      id: 'f-at-a',
      lhs: 'f(a)',
      relation: '=' as const,
      rhs: formatNum(fa),
      emphasis: faIsBroken ? ('broken' as const) : ('neutral' as const),
    },
    {
      id: 'kind',
      lhs: 'kind',
      relation: '=' as const,
      rhs: kind,
    },
  ];
}

// ─── Spec factory ─────────────────────────────────────────────────────────────

function buildSpec(): ExperimentSpec<LimitLabState, CalcKey, CalculusContext> {
  // Closure-stashed handles created in setup():
  let plane: CartesianPlaneHandle;
  let figure: FigureHandle;
  let curves: CurveLayerHandle;
  let readout: ReadoutHandle;
  let viewport: CartesianViewportHandle;
  let legend: FormulaLegendHandle;
  let activePresetId = LIMIT_PRESETS[0]!.id;
  // Extra SVG elements for the ε–δ bands (advanced mode):
  let epsilonGroup: SVGGElement | null = null;

  function defaultState(_ctx: CalculusContext): LimitLabState {
    // Seed the approach slightly away from the preset discontinuity point
    const preset = LIMIT_PRESETS[0]!;
    return {
      presetId: preset.id,
      approach: preset.point + 1,
      showEpsilonDelta: false,
      epsilon: 0.5,
    };
  }

  function applyPresetLocal(state: LimitLabState, id: string): LimitLabState {
    const preset = LIMIT_PRESETS.find((p) => p.id === id);
    if (!preset) return state;
    return { ...state, presetId: preset.id, approach: preset.point + 1 };
  }

  function getPreset(state: LimitLabState) {
    return LIMIT_PRESETS.find((p) => p.id === state.presetId) ?? LIMIT_PRESETS[0]!;
  }

  return {
    labelKey: 'experiment.limit-lab',
    demoId: 'calculus',
    advancedKey: 'limit-lab',

    initialState(ctx): LimitLabState {
      return defaultState(ctx);
    },

    applyPreset(state, id): LimitLabState {
      return applyPresetLocal(state, id);
    },

    setup(ctx, controller: HarnessController<LimitLabState>): void {
      plane = createCartesianPlane(controller.svg, { domains: BASE_DOMAINS, plotRect: PLOT_RECT });
      controller.registerCleanup(() => plane.destroy());
      curves = createCurveLayer(plane.content, plane);
      controller.registerCleanup(() => curves.destroy());
      figure = controller.createFigure();
      readout = controller.createReadout();
      legend = createFormulaLegend(ctx.root);
      controller.registerCleanup(() => legend.destroy());
      viewport = createCartesianViewport(controller.svg, {
        base: BASE_DOMAINS,
        plotRect: PLOT_RECT,
        onChange(next) {
          plane.setDomains(next);
          controller.requestRender();
        },
      });
      controller.registerCleanup(() => viewport.dispose());
    },

    render(state: LimitLabState, ctx): void {
      const preset = getPreset(state);
      if (preset.id !== activePresetId) {
        activePresetId = preset.id;
        viewport.setBase(BASE_DOMAINS, { notify: false });
        plane.setDomains(BASE_DOMAINS);
      }
      const a = preset.point;
      const approach = state.approach;
      const f = compileFn(preset.source);

      // ── Function curve (broken at the discontinuity point) ──
      // Sample the domain split around the approach point to avoid the discontinuity.
      // We sample left and right of `a` separately so the curve breaks there.
      const domains = plane.getDomains();
      const leftPts =
        domains.xDomain[0] < a
          ? sampleFn(f, domains.xDomain[0], Math.min(a - 0.01, domains.xDomain[1]), SAMPLE_N / 2)
          : [];
      const rightPts =
        domains.xDomain[1] > a
          ? sampleFn(f, Math.max(a + 0.01, domains.xDomain[0]), domains.xDomain[1], SAMPLE_N / 2)
          : [];
      // Inject a NaN break between the two halves so the curve layer lifts its pen.
      const curvePts: Vec2[] = [...leftPts, { x: NaN, y: NaN }, ...rightPts];
      curves.setPolyline('f', curvePts, 'calc-curve-primary');

      // ── Left/right approach dots ──
      const approachLeft = approach - APPROACH_OFFSET;
      const approachRight = approach + APPROACH_OFFSET;
      const yLeft = f(approachLeft);
      const yRight = f(approachRight);
      const screenLeft = plane.worldToScreen({ x: approachLeft, y: yLeft });
      const screenRight = plane.worldToScreen({ x: approachRight, y: yRight });

      // ── Cursor on x-axis + vertical guide ──
      const screenCursor = plane.worldToScreen({ x: approach, y: 0 });
      const screenGuideTop = plane.worldToScreen({ x: approach, y: domains.yDomain[1] });
      const screenGuideBottom = plane.worldToScreen({ x: approach, y: domains.yDomain[0] });

      const elements: FigureElement[] = [
        // Draggable cursor on the x-axis
        {
          kind: 'point',
          id: 'pt-x',
          at: screenCursor,
          style: { className: 'geo-point-draggable' },
          draggable: {},
        },
        // Vertical guide line through the approach point
        {
          kind: 'segment',
          id: 'guide-vert',
          from: 'guide-top',
          to: 'guide-bottom',
          style: { className: 'geo-line geo-line--faint', dashed: true },
        },
        {
          kind: 'point',
          id: 'guide-top',
          at: screenGuideTop,
          style: { className: 'geo-point-off-stage' },
        },
        {
          kind: 'point',
          id: 'guide-bottom',
          at: screenGuideBottom,
          style: { className: 'geo-point-off-stage' },
        },
      ];

      // Approach dots — only when yLeft/yRight are finite
      if (Number.isFinite(yLeft)) {
        elements.push({
          kind: 'point',
          id: 'dot-left',
          at: screenLeft,
          style: { className: 'geo-point-main' },
        });
      }
      if (Number.isFinite(yRight)) {
        elements.push({
          kind: 'point',
          id: 'dot-right',
          at: screenRight,
          style: { className: 'geo-point-secondary' },
        });
      }

      figure.update(elements);
      curves.group.ownerSVGElement
        ?.querySelector('[data-figure-id="pt-x"]')
        ?.setAttribute('data-no-pan', 'true');
      legend.setEntries([{ id: 'f', formula: `f(x) = ${preset.source}`, color: 'var(--accent)' }]);

      // ── Readout ──
      readout.update(limitReadoutModel(state));

      // ── ε–δ band (advanced mode) ──
      if (epsilonGroup) {
        epsilonGroup.remove();
        epsilonGroup = null;
      }

      if (ctx.advanced && state.showEpsilonDelta) {
        const L = oneSidedLimit(f, a, 'left');
        const R = oneSidedLimit(f, a, 'right');
        // Use the two-sided limit if it exists, otherwise skip the band.
        if (Number.isFinite(L) && Number.isFinite(R) && Math.abs(L - R) < 1e-4) {
          const limit = (L + R) / 2;
          const eps = state.epsilon;
          const bandTop = plane.worldToScreen({ x: domains.xDomain[0], y: limit + eps });
          const bandBottom = plane.worldToScreen({ x: domains.xDomain[1], y: limit - eps });
          const limitScreen = plane.worldToScreen({ x: 0, y: limit });

          epsilonGroup = createSvgEl('g', { class: 'calc-epsilon-band' });
          // Epsilon band (filled rect between limit ± eps)
          const bandRect = createSvgEl('rect', {
            x: String(0),
            y: String(Math.min(bandTop.y, bandBottom.y)),
            width: String(STAGE_W),
            height: String(Math.abs(bandTop.y - bandBottom.y)),
            class: 'calc-area',
          });
          epsilonGroup.appendChild(bandRect);
          // Limit line (L)
          const limitLine = createSvgEl('line', {
            x1: String(0),
            y1: String(limitScreen.y),
            x2: String(STAGE_W),
            y2: String(limitScreen.y),
            class: 'calc-curve-secondary',
            'stroke-dasharray': '6 4',
          });
          epsilonGroup.appendChild(limitLine);
          // Top epsilon line
          const topLine = createSvgEl('line', {
            x1: String(0),
            y1: String(bandTop.y),
            x2: String(STAGE_W),
            y2: String(bandTop.y),
            class: 'calc-curve-faint',
          });
          epsilonGroup.appendChild(topLine);
          // Bottom epsilon line
          const bottomLine = createSvgEl('line', {
            x1: String(0),
            y1: String(bandBottom.y),
            x2: String(STAGE_W),
            y2: String(bandBottom.y),
            class: 'calc-curve-faint',
          });
          epsilonGroup.appendChild(bottomLine);

          plane.content.insertBefore(epsilonGroup, curves.group);
        }
      }
    },

    onDrag(state: LimitLabState, pointId: string, at: Vec2): LimitLabState {
      if (pointId === 'pt-x') {
        return { ...state, approach: plane.screenToWorld(at).x };
      }
      return state;
    },

    panel(state: LimitLabState, ctx): PanelRow<CalcKey, LimitLabState>[] {
      const rows: PanelRow<CalcKey, LimitLabState>[] = [];

      // Preset dropdown
      rows.push({
        kind: 'dropdown',
        id: 'limit-preset',
        labelKey: 'limit.panel.preset',
        options: LIMIT_PRESETS.map((p) => ({ value: p.id, labelKey: p.labelKey })),
        value: state.presetId,
        onChange: (v) => applyPresetLocal(state, v),
      });

      // Approach slider
      const preset = getPreset(state);
      const a = preset.point;
      rows.push({
        kind: 'slider',
        id: 'limit-approach',
        labelKey: 'limit.panel.approach',
        min: a - 3,
        max: a + 3,
        step: 0.05,
        value: state.approach,
        format: (v) => v.toFixed(2),
        onChange: (v) => ({ ...state, approach: v }),
      });

      // Advanced toggle
      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      // Advanced: epsilon slider
      if (ctx.advanced) {
        rows.push({
          kind: 'toggle',
          labelKey: 'limit.panel.show-epsilon-delta',
          value: state.showEpsilonDelta,
          onChange: (v) => ({ ...state, showEpsilonDelta: v }),
        });
        rows.push({
          kind: 'slider',
          id: 'limit-epsilon',
          labelKey: 'limit.panel.epsilon',
          min: 0.05,
          max: 2,
          step: 0.05,
          value: state.epsilon,
          format: (v) => v.toFixed(2),
          onChange: (v) => ({ ...state, epsilon: v }),
        });
      }

      return rows;
    },

    actions: (_state: LimitLabState, ctx): ActionBarSpec<CalcKey, LimitLabState> => {
      return {
        onReset: () => defaultState(ctx),
        onRandomise: (s) => {
          const idx = Math.floor(ctx.prng() * LIMIT_PRESETS.length);
          const preset = LIMIT_PRESETS[idx]!;
          return { ...s, presetId: preset.id, approach: preset.point + 1 };
        },
        presets: LIMIT_PRESETS.map((p) => ({ id: p.id, labelKey: p.labelKey })),
        onPreset: (s, id) => applyPresetLocal(s, id),
        viewReset: { labelKey: 'action.reset-view', onResetView: () => viewport.reset() },
      };
    },

    applyShotState(state: LimitLabState, _shotId: string): LimitLabState {
      const preset = getPreset(state);
      return {
        ...state,
        approach: preset.point + 0.8,
        showEpsilonDelta: true,
        epsilon: 0.5,
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
